import { enableProdMode, provideZoneChangeDetection, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';
import { ServiceWorkerModule } from '@angular/service-worker';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { ToastrModule } from 'ngx-toastr';
import { PixelModule } from '@components/facebook-pixel/pixel.module';
import { AuthenticationService } from '@services/authentication.service';
import { LanguageChangeService } from '@services/language-change.service';
import { RouteLoaderService } from '@services/route-loader.service';
import { WindowRefService } from '@services/window-ref.service';
import { ActionWatcherFactory } from '@extensions/action-watcher.factory';
import { ActionWatcherService } from '@services/action-watcher.service';
import { RestClient } from './app/extensions/rest.client';
import { HeaderInterceptor } from './app/interceptors/header.interceptor';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { ApplicationSettingsState } from '@store/application-settings.state';
import { LanguagesState } from '@store/languages.state';
import { ModuleLoadState } from '@store/module-load.state';
import { NotificationsState } from '@store/notifications.state';
import { ProfilesState } from '@store/profiles.state';
import { ToastrState } from '@store/toastr.state';
import { TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';

registerLocaleData(localeEs);
registerLocaleData(localePt);

function initializeAuthentication(authenticationService: AuthenticationService): () => Promise<void> {
  return () => authenticationService.initialize();
}

const DEVTOOLS_IMPORTS = environment.production
  ? []
  : [NgxsReduxDevtoolsPluginModule.forRoot({ maxAge: 25 })];

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      NgxsModule.forRoot([
        ToastrState,
        ModuleLoadState,
        ProfilesState,
        LanguagesState,
        NotificationsState,
        ApplicationSettingsState
      ], {
        developmentMode: !environment.production,
        selectorOptions: {
          suppressErrors: environment.production
        },
        compatibility: {
          strictContentSecurityPolicy: true
        },
      }),
      NgxsStoragePluginModule.forRoot({
        keys: ['profiles.selectedLanguage'],
        storage: StorageOption.SessionStorage
      }),
      ...DEVTOOLS_IMPORTS,
      NgxsRouterPluginModule.forRoot(),
      NgxsFormPluginModule.forRoot(),
      PixelModule.forRoot({ enabled: true, pixelId: environment.facebookPixelId }),
      ReactiveFormsModule,
      NgSelectModule,
      NgxGoogleAnalyticsModule.forRoot(environment.googleAnalyticsTag),
      NgxGoogleAnalyticsRouterModule,
      BrowserAnimationsModule,
      ToastrModule.forRoot({
        closeButton: true,
        maxOpened: 3,
        preventDuplicates: true,
        resetTimeoutOnDuplicate: true,
        onActivateTick: true,
        easing: 'ease-in-out',
        positionClass: 'toast-top-right',
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslateHttpLoader
        }
      }),
      AppRoutingModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
      })
    ),
    RouteLoaderService,
    LanguageChangeService,
    TranslateService,
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: './assets/i18n/',
        suffix: '.json'
      }
    },
    {
      provide: APP_INITIALIZER,
      useFactory: ActionWatcherFactory,
      deps: [ActionWatcherService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthentication,
      deps: [AuthenticationService],
      multi: true,
    },
    WindowRefService,
    {
      provide: RestClient,
      useFactory: () => new RestClient(),
      deps: [HttpClient],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HeaderInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
}).catch(err => console.error(err));
